import React, { useEffect, useMemo, useState } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import ReactTable from 'react-table';
import moment from "moment";
import momentTimezone from 'moment-timezone';
import Select from 'react-select';
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import axios from "../../../../axios-instance";
import queryString from "query-string";

import { getEventsStatistics } from '../../../../store/actions/eventsActions.js';
import NewLoader from '../../../../components/NewLoader/NewLoader.js';
import { PeriodSelector } from '../../../../components/Statistics/PeriodSelector/PeriodSelector.jsx';
import { StatisticInfo } from '../../../../components/Statistics/StatisticInfo/StatisticInfo.jsx';
import { StatisticBarChart } from '../../../../components/Statistics/StatisticBarChart/StatisticBarChart.jsx';
import PleaseWaitModal from "../../../BarCodes/modals/PleaseWaitModal.js";
import { loadFile } from "../../../../helpers/loadFile";
import {
  period,
  paymentOptions,
} from '../../../../components/Statistics/constants.js';
import { getInitialStatisticValues } from '../../../../components/Statistics/helpers/getInitialStatisticValues.js';
import { getAnalyzedData } from '../../../../components/Statistics/helpers/getAnalyzedData.js';

import "./PromoterStatistics.sass";
import { showTicketsInfoModal } from '../../../../components/Statistics/helpers/showTicketsInfoModal.js';
import { Link } from 'react-router-dom';
import ROUTES from '../../../../constants/routes.js';
import { getCustomURLs } from "../../../../helpers/getCustomURLs.js";

const INIT_START_DATE = moment().utc().startOf("week").toISOString()
const INIT_END_DATE = moment().utc().toISOString();
const MIN_DATE = moment().utc().add("y", -2).toISOString();
const PERIODS_LIST = period.slice(1);
const MySwal = withReactContent(Swal);
const { API_URL } = getCustomURLs();

const customSelectStyles = {
  control: (base) => ({
    ...base,
    height: 'max-content',
    'min-height': '46px',
    'max-height': '100px',
    cursor: 'pointer',
    overflow: 'auto',
  }),
  placeholder: (base) => ({
    ...base,
    color: '#ABABAB',
  }),
};

export const PromoterStatistics = ({
  getEventsStatistics,
  token
}) => {
  const [data, setData] = useState([]);
  const [eventsList, setEventsList] = useState([])
  const [selectedEvents, setSelectedEvents] = useState([])
  const [isLoading, setLoading] = useState(true);
  const [isFirstLoaded, setFirstLoaded] = useState(false);
  const [isTokenLoaded, setTokenLoaded] = useState(false);

  const [searchPeriod, setSearchPeriod] = useState(period[2]);
  const [eventData, setEventData] = useState({ periodStart: INIT_START_DATE, periodEnd: INIT_END_DATE });

  const [ticketsInfo, setTicketsInfo] = useState({});
  const [usersInfo, setUsersInfo] = useState({});
  const [tableData, setTableData] = useState([]);


  const getData = async (startDate, endDate) => {
    setLoading(true);

    try {
      const { transactions, events } = await getEventsStatistics({
        eventId: '',
        startDate,
        endDate
      });

      const { dataWithEventTimezone, periodStart, periodEnd, ...generalEvenData } =
        getInitialStatisticValues(transactions, events[0].eventTimeZone, 5, eventData.periodStart, eventData.periodEnd);

      const dataForEventsList = [];

      for (const event of events) {
        dataForEventsList.push({
          value: event.eventId,
          label: event.eventName,
          eventTimeZone: event.eventTimeZone,
          currency: event.currency,
        })
      };
      

      setEventsList(dataForEventsList);
      setData(dataWithEventTimezone);
      setEventData(prev => ({...prev, ...generalEvenData, minDate: MIN_DATE }));
    } catch {
      setLoading(false);
    }
  };

  const dataPerPeriod = useMemo(() => {
    const { periodStart, periodEnd } = eventData;

    if (selectedEvents.length !== 0) {
      const currCurrency = selectedEvents[0].currency;

      return data.filter(tr => {
        return selectedEvents.some(e => e.value === tr.eventId) &&
          tr.paymentDate >= periodStart &&
          tr.paymentDate <= periodEnd &&
          tr.currency === currCurrency;
      });
    }

    return data.filter((tr) => {
      return tr.paymentDate >= periodStart && tr.paymentDate <= periodEnd;
    });
  }, [eventData, data, selectedEvents]);

  const currPeriod = useMemo(() => {
    return ({
      start: eventData.periodStart,
      end: eventData.periodEnd
    });
  }, [eventData.periodStart, eventData.periodEnd]);

  const handleChangeEvent = (item) => {
    setSelectedEvents(item);
  };

  const downloadReportHandler = () => {
    const fileName = currPeriod.start + "_" + currPeriod.end + "-global-statistics";

    MySwal.fire({
      html: <PleaseWaitModal message='Downloading file…' />,
      customClass: 'please-wait-modal',
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
    });
    const utcOffset = -1 * moment.tz(eventsList[0].eventTimeZone).utcOffset();
    const formatString = "YYYY-MM-DD HH:mm:ss";
    const query = queryString.stringify({ asCsv : true, start : moment(currPeriod.start).add(utcOffset, 'minutes').toISOString(false), end : moment(currPeriod.end).add(utcOffset, 'minutes').toISOString(false) }, { encode: true })
    axios({
      method: "POST",
      url: API_URL + `/EventReports/ByTicketTemplatesAtEvents?${query}`,
      responseType: "arraybuffer",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      data: (!selectedEvents || selectedEvents.length == 0) ? eventsList.map(e => e.value) : selectedEvents.map(e => e.value)
    })
      .then((response) => {
        loadFile(
          response.data,
          `${fileName}_${moment().format("MM_DD_HH_mm")}.csv`
        );
      })
      .catch((error) => console.log(error))
      .finally(() => {
        MySwal.close();
      });
  };

  // Getting analyzed data
  useEffect(() => {
    const { ticketsInfo, usersInfo, tableData } = getAnalyzedData(
      dataPerPeriod,
      paymentOptions
    );

    setTicketsInfo(ticketsInfo);
    setUsersInfo(usersInfo);
    setTableData(tableData);

    setLoading(false);
  }, [dataPerPeriod]);

  // Getting data from server
  useEffect(() => {
    if (isTokenLoaded) {
      getData(INIT_START_DATE, INIT_END_DATE);
      setFirstLoaded(true);
    }
  }, [isTokenLoaded]);

  useEffect(() => {
    setTokenLoaded(true);
    
    if (isFirstLoaded) {
      getData(currPeriod.start, currPeriod.end);
    }
  }, [currPeriod]);

  const linkProps = () => ({
    style: {
      padding: "0",
    },
    onClick: (e) => {
      e.stopPropagation();
    },
  })

  const columns = [
    {
      Header: 'Ticket name',
      Cell: ({
        row: {
          _original: { eventId, ticketTemplateId, ticketName },
        },
      }) => {
        return (
          <Link
            to={ROUTES.NEW_TICKET_DETAIL.replace(":ticket_id", ticketTemplateId).replace(":id", eventId)}
          >
            {ticketName}
          </Link>
        );
      },
      getProps: linkProps,
      resizable: false,
    },
    {
      Header: 'Event name',
      Cell: ({
        row: {
          _original: { eventId },
        },
      }) => {
        return (
          <Link
            to={ROUTES.NEW_EVENT_INFO.replace(":id", eventId)}
          >
            {eventsList.find(e => e.value === eventId).label || ''}
          </Link>
        );
      },
      getProps: linkProps,
      resizable: false,
      width: 240,
    },
    {
      Header: 'Average age',
      Cell: ({
        row: {
          _original: { male, female, undefinedGender, totalAge },
        },
      }) => {
        return (
          <>
            {Math.round(totalAge / (male + female + undefinedGender)) || 'N/A'}
          </>
        );
      },
      resizable: false,
      width: 120,
    },
    {
      Header: 'Male',
      Cell: ({
        row: {
          _original: { male, female, undefinedGender, totalAge },
        },
      }) => {
        if (!male && !female && !undefinedGender) {
          return <>N/A</>;
        }
        return <>{male}</>;
      },
      resizable: false,
      width: 100,
    },
    {
      Header: 'Female',
      Cell: ({
        row: {
          _original: { male, female, undefinedGender, totalAge },
        },
      }) => {
        if (!male && !female && !undefinedGender) {
          return <>N/A</>;
        }
        return <>{female}</>;
      },
      resizable: false,
      width: 100,
    },
    {
      Header: 'Amount',
      accessor: 'ticketsAmount',
      resizable: false,
      width: 100,
    },
    {
      Header: `Paid, ${eventData.currency}`,
      accessor: 'totalPaid',
      resizable: false,
      width: 140,
    },
  ];

  if (!!usersInfo.undefinedGender) {
    columns.splice(4, 0, {
      Header: 'Undefined gender',
      accessor: 'undefinedGender',
      resizable: false,
      width: 140,
    });
  }

  if (isLoading) {
    return <NewLoader />;
  }

  return (
    <section className='promoter-statistics'>
      <div className='promoter-statistics__header'>
        <div className='promoter-statistics__left-header-block'>
          <PeriodSelector
            searchPeriod={searchPeriod}
            setSearchPeriod={setSearchPeriod}
            eventData={eventData}
            setEventData={setEventData}
            period={PERIODS_LIST}
          />

          <div className="add-event__block promoter-statistics__select">
            <label>Events</label>
            <Select
              options={eventsList}
              isMulti
              placeholder='Select events'
              name='searchProfilePeriod'
              classNamePrefix='custom-select'
              styles={customSelectStyles}
              value={selectedEvents}
              onChange={handleChangeEvent}
              className='custom-select'
              theme={(theme) => ({
                ...theme,
                colors: {
                  ...theme.colors,
                  primary: '#6071B5',
                  primary25: '#F2F4FE',
                  primary50: '#F2F4FE',
                  primary75: '#F2F4FE',
                },
              })}
            />
          </div>
          <button
            className='btn-primary'
            onClick={downloadReportHandler}
          >
            Download Report
          </button>
        </div>

        <article className='promoter-statistics__info'>
          <StatisticInfo
            paymentOptions={paymentOptions}
            ticketsInfo={ticketsInfo}
            usersInfo={usersInfo}
          />
        </article>
      </div>

      {isFirstLoaded && dataPerPeriod.length === 0 ? (
        <div>No data to display</div>
      ) : (
        <>
          <div className="add-event__block promoter-statistics__events-info">
            <label>Statistics info</label>
            <ul className="promoter-statistics__events-info-list">
              <li>
                Timezone: {
                  eventsList.length !== 0 ?
                  momentTimezone().tz(eventsList[0].eventTimeZone).format('Z') + " - " + eventsList[0].eventTimeZone
                  : ''
                  }
              </li>
              <li>
                {
                  selectedEvents.length === 0 ?
                  `Default currency is ${eventData.currency}. Select event for currency initialization.` :
                  'Current currency is ' + eventData.currency + '. Event with others currencies are not shown in events list.'
                }
              </li>
            </ul>
          </div>
          <article className='promoter-statistics__chart'>
            <StatisticBarChart
              searchPeriod={searchPeriod}
              dataPerPeriod={dataPerPeriod}
              eventData={eventData}
              eventsList={eventsList}
            />
          </article>

          <ReactTable
            data={tableData}
            columns={columns}
            showPagination={false}
            minRows={0}
            sortable={false}
            defaultPageSize={9999999}
            getTrProps={(state, { original }) => ({
              onClick: () => {
                showTicketsInfoModal(original.paymentTypes);
              },
            })}
          />
        </>
      )}
    </section>
  );
};

const mapStateToProps = ({ events: { currentEvent }, auth: { user } }) => ({
  currentEvent,
  user,
  token: user.token,
});

const mapDispatchToProps = (dispatch) => ({
  getEventsStatistics: ({ eventId, startDate, endDate }) =>
    dispatch(getEventsStatistics({ eventId, startDate, endDate })),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(PromoterStatistics)
);
